// src/shared/utils/persistedForm.ts
//
// Shared plumbing for persisted (localStorage-backed) Zustand form stores --
// the "latest inputs survive navigation / refresh" pattern used by the PRO
// Drift Auditor and the PRO Diversification Optimizer.
//
//   - `createSafeLocalStorage` is a zustand `PersistStorage` adapter that never
//     throws: private mode / disabled storage, quota errors and corrupt JSON
//     all degrade to "no saved state".
//   - The guards below let each store's `merge` / `migrate` re-validate the
//     untrusted persisted payload field-by-field without any `any` casts.

import type { PersistStorage, StorageValue } from 'zustand/middleware';

export interface Bound {
    min: number;
    max: number;
    step?: number;
}

export const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

export const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

// Clamp an untrusted value into [min, max] (falling back when it isn't a finite
// number) and snap it to `step` when the bound defines one.
export const clampToBound = (v: unknown, fallback: number, { min, max, step }: Bound): number => {
    const x = Math.min(max, Math.max(min, isFiniteNumber(v) ? v : fallback));
    return step ? Math.min(max, Math.max(min, Number((Math.round(x / step) * step).toFixed(4)))) : x;
};

export const createSafeLocalStorage = <T>(): PersistStorage<T> => ({
    getItem: (name) => {
        try {
            const raw = window.localStorage.getItem(name);
            if (!raw) return null;
            const parsed: unknown = JSON.parse(raw);
            if (isRecord(parsed) && 'state' in parsed) return parsed as StorageValue<T>;
            window.localStorage.removeItem(name);
        } catch {
            try {
                window.localStorage.removeItem(name);
            } catch {
                /* storage unavailable -- nothing to clean up */
            }
        }
        return null;
    },
    setItem: (name, value) => {
        try {
            window.localStorage.setItem(name, JSON.stringify(value));
        } catch {
            /* quota exceeded / storage unavailable -- keep working in memory */
        }
    },
    removeItem: (name) => {
        try {
            window.localStorage.removeItem(name);
        } catch {
            /* storage unavailable */
        }
    },
});
